import type { Machine } from "@/features/line-machines/public";
import {
  clampPressPosition,
  DEFAULT_SPAWN_INTERVAL_SEC,
  getArmTile,
  getPressPathDist,
  getPressTile,
  getTransportParking,
  LAYOUT_BOUNDS,
  LOADING_TILE,
  MIN_WORK_SPACING,
  UNLOAD_DURATION_SEC,
  UNLOAD_TILE,
} from "@/features/simulation/constants";
import { createDefaultScene, deriveLayoutFromScene } from "@/features/simulation/scene";
import type { FactoryScene } from "@/features/simulation/scene-types";
import {
  DEFAULT_SIM_CONTEXT,
  type Layout,
  type RobotArm,
  type SimulationContext,
  type SimulationParams,
  type SimulationState,
  type SimulationStats,
  type SpawnMode,
  type TransportRobot,
  type Work,
} from "@/features/simulation/types";
import { clampInt } from "@/lib/math/clamp";

/**
 * シミュレーションエンジンの純粋関数群。
 * ここには React も Pixi も Zustand も登場させない — テスタビリティを担保。
 *
 * ラインのジオメトリは state.layout から都度導出（constants.ts のヘルパを経由）。
 */

export function createInitialState(overrides?: {
  spawnMode?: SpawnMode;
  scene?: FactoryScene;
  layout?: Layout;
}): SimulationState {
  // 配置情報のソース解決: scene > layout > default
  let scene: FactoryScene;
  if (overrides?.scene) {
    scene = overrides.scene;
  } else if (overrides?.layout) {
    scene = createDefaultScene({
      conveyorLength: overrides.layout.conveyorLength,
      pressColumn: overrides.layout.pressPosition,
      bufferCapacity: overrides.layout.bufferCapacity,
      transportCapacity: overrides.layout.transportCapacity,
    });
  } else {
    scene = createDefaultScene();
  }

  const derived = deriveLayoutFromScene(scene);
  const layout: Layout = {
    conveyorLength: derived.conveyorLength,
    pressPosition: clampPressPosition(derived.pressPosition, derived.conveyorLength),
    bufferCapacity: clampInt(
      derived.bufferCapacity,
      LAYOUT_BOUNDS.bufferCapacity.min,
      LAYOUT_BOUNDS.bufferCapacity.max,
    ),
    transportCapacity: clampInt(
      derived.transportCapacity,
      LAYOUT_BOUNDS.transportCapacity.min,
      LAYOUT_BOUNDS.transportCapacity.max,
    ),
  };

  // Scene の press / arm / transport から複数インスタンスを構築 (P3)
  const machines: Machine[] = scene.presses.map((p) => {
    const conveyor = scene.conveyors.find((c) => c.id === p.conveyorId);
    const pathDist = conveyor
      ? // path 上のタイル一致を線形探索
        (() => {
          for (let i = 0; i < conveyor.path.length; i++) {
            const t = conveyor.path[i];
            if (t && t.col === p.position.col && t.row === p.position.row) return i;
          }
          return 0;
        })()
      : 0;
    return {
      id: p.id,
      kind: "press" as const,
      position: { ...p.position },
      pathDist,
      processingTime: 2,
      busy: false,
      currentWorkId: null,
      progress: 0,
    };
  });

  // 後方互換: scene が空でも press 1 個は確保 (旧テスト互換用)
  if (machines.length === 0) {
    const pressTile = getPressTile(layout);
    machines.push({
      id: "press-1",
      kind: "press",
      position: { ...pressTile },
      pathDist: getPressPathDist(layout),
      processingTime: 2,
      busy: false,
      currentWorkId: null,
      progress: 0,
    });
  }

  const arms: RobotArm[] = scene.arms.map((a) => ({
    id: a.id,
    position: { ...a.position },
    phase: "idle" as const,
    timer: 0,
    heldWorkId: null,
    armAngle: ARM_IDLE_ANGLE,
  }));
  if (arms.length === 0) {
    const armTile = getArmTile(layout);
    arms.push({
      id: "arm-1",
      position: { ...armTile },
      phase: "idle",
      timer: 0,
      heldWorkId: null,
      armAngle: ARM_IDLE_ANGLE,
    });
  }

  const transports: TransportRobot[] = scene.transports.map((t) => ({
    id: t.id,
    phase: "idle_at_line" as const,
    position: { col: t.parking.col, row: t.parking.row },
    waypointIndex: 0,
    unloadTimer: 0,
    cargo: [],
  }));
  if (transports.length === 0) {
    const parking = getTransportParking(layout);
    transports.push({
      id: "transport-1",
      phase: "idle_at_line",
      position: { col: parking.col, row: parking.row },
      waypointIndex: 0,
      unloadTimer: 0,
      cargo: [],
    });
  }

  // arm / transport は arms[0] / transports[0] への別名 (旧式テスト互換、同一参照)
  const arm = arms[0];
  const transport = transports[0];
  if (!arm || !transport) {
    throw new Error("createInitialState: arms/transports must have at least 1 element");
  }

  const stats: SimulationStats = {
    completedCount: 0,
    lastCycleTimeSec: null,
    throughputPerMin: 0,
    recentCompletionTimes: [],
  };

  return {
    time: 0,
    scene,
    layout,
    works: [],
    machines,
    arms,
    transports,
    arm,
    transport,
    stats,
    spawnMode: overrides?.spawnMode ?? { kind: "auto", intervalSec: DEFAULT_SPAWN_INTERVAL_SEC },
    spawnTimer: 0,
    queuedSpawns: 0,
    nextWorkId: 1,
    conveyorStopped: false,
    conveyorStoppedAt: null,
  };
}

/**
 * 指定モードに応じてワーク1個の投入を試みる。投入可能なら state を書き換える。
 * P3: isEntry なコンベアの先頭タイルにワークを置く。複数ある場合は最初を採用。
 */
function trySpawnWork(state: SimulationState): void {
  const entryConveyor = state.scene.conveyors.find((c) => c.isEntry) ?? state.scene.conveyors[0];
  if (!entryConveyor) return; // コンベアが無い → 投入不可
  const conveyorId = entryConveyor.id;

  // そのコンベア上の pre_press 先頭との間隔チェック
  const front = state.works
    .filter((w) => w.conveyorId === conveyorId && w.phase === "pre_press")
    .reduce((min, w) => (w.pathDist < min ? w.pathDist : min), Number.POSITIVE_INFINITY);
  if (front < MIN_WORK_SPACING) {
    return; // 先頭に空きがない
  }
  const w: Work = {
    id: `w-${state.nextWorkId}`,
    phase: "pre_press",
    conveyorId,
    pathDist: 0,
    phaseTimer: 0,
    defectStatus: "ok",
    dwellTime: 0,
    currentStallSec: 0,
    bufferSlot: null,
    createdAt: state.time,
  };
  state.works.push(w);
  state.nextWorkId += 1;
}

/**
 * 停止中のコンベアをユーザー操作で再開する。
 * 全ワークの滞留計測をリセットして、即座に再停止するのを防ぐ。
 */
export function resumeConveyor(state: SimulationState): void {
  state.conveyorStopped = false;
  state.conveyorStoppedAt = null;
  for (const w of state.works) {
    w.currentStallSec = 0;
  }
}

/** Start/Resume時などに外部から手動で1個投入を要求する */
export function requestManualSpawn(state: SimulationState): void {
  if (state.spawnMode.kind === "manual") {
    trySpawnWork(state);
  } else if (state.spawnMode.kind === "queued") {
    if (state.queuedSpawns < state.spawnMode.maxN) {
      state.queuedSpawns += 1;
    }
  }
}

/**
 * tick 1ステップを進める（mutates state）。
 *
 * `ctx.permissions` が `false` の系統は当該フレームで進行をスキップする
 * ＝ 進行中の動作も次フレームで凍結（"オフラインの装置だけ即時停止"）。
 *
 * 既存テストとの互換のため `ctx` は省略可で、省略時は全許可。
 */
export function tick(
  state: SimulationState,
  params: SimulationParams,
  dtSec: number,
  ctx: SimulationContext = DEFAULT_SIM_CONTEXT,
): void {
  if (dtSec <= 0) return;
  state.time += dtSec;

  // バッファ満杯時はコンベアを自動停止、空きが出たら自動再開
  syncConveyorStoppedFromBuffer(state);

  // 順序の意図:
  //  1. プレスの進行（前フレーム busy だったプレスがこのフレームで完了する可能性 → 完了させる）
  //  2. handleSpawn / updateConveyorWorks（プレス完了後に動かしたいので、プレスの後に置く）
  //  これにより、プレス完了の瞬間に下流ワークが流れ始める＋次のワークがプレスへ入れる。
  if (ctx.permissions.pressEnabled) {
    updatePress(state, params, dtSec);
  }
  if (ctx.permissions.spawnEnabled) {
    handleSpawn(state, dtSec);
  }
  updateConveyorWorks(state, params, dtSec, ctx);
  if (ctx.permissions.armEnabled) {
    updateArm(state, params, dtSec);
  }
  if (ctx.permissions.transportEnabled) {
    updateTransport(state, params, dtSec);
  }
  updateStats(state);
}

/**
 * 各コンベアの buffer 占有を見て、いずれか満杯なら conveyorStopped=true。
 * (グローバルフラグ。per-conveyor 制御は将来拡張)
 */
function syncConveyorStoppedFromBuffer(state: SimulationState): void {
  let anyFull = false;
  for (const conveyor of state.scene.conveyors) {
    let inBufferCount = 0;
    for (const w of state.works) {
      if (w.conveyorId === conveyor.id && w.phase === "in_buffer") inBufferCount += 1;
    }
    if (inBufferCount >= conveyor.bufferCapacity) {
      anyFull = true;
      break;
    }
  }
  if (anyFull && !state.conveyorStopped) {
    state.conveyorStopped = true;
    state.conveyorStoppedAt = state.time;
  } else if (!anyFull && state.conveyorStopped) {
    state.conveyorStopped = false;
    state.conveyorStoppedAt = null;
  }
}

function handleSpawn(state: SimulationState, dtSec: number): void {
  // コンベア停止中 or プレス稼働中は新規投入を抑制
  if (state.conveyorStopped) return;
  if (isAnyPressBusy(state)) return;

  if (state.spawnMode.kind === "auto") {
    state.spawnTimer += dtSec;
    while (state.spawnTimer >= state.spawnMode.intervalSec) {
      state.spawnTimer -= state.spawnMode.intervalSec;
      trySpawnWork(state);
    }
  } else if (state.spawnMode.kind === "queued") {
    if (state.queuedSpawns > 0) {
      const before = state.works.length;
      trySpawnWork(state);
      if (state.works.length > before) {
        state.queuedSpawns -= 1;
      }
    }
  }
}

function updateConveyorWorks(
  state: SimulationState,
  params: SimulationParams,
  dtSec: number,
  ctx: SimulationContext,
): void {
  // コンベア停止中はベルト全体が止まる (グローバルフラグ)。
  if (state.conveyorStopped) return;

  for (const conveyor of state.scene.conveyors) {
    updateConveyorWorksForConveyor(state, conveyor, params, dtSec, ctx);
  }
}

/** 1 本のコンベア上のワーク進行 (pre_press / post_press) を処理 */
function updateConveyorWorksForConveyor(
  state: SimulationState,
  conveyor: { id: string; path: { col: number; row: number }[]; bufferCapacity: number },
  params: SimulationParams,
  dtSec: number,
  ctx: SimulationContext,
): void {
  // このコンベア上にあるプレスを取得 (現状は最大 1 個と仮定)
  const pressMachineForConveyor = findPressMachineOnConveyor(state, conveyor.id);
  if (pressMachineForConveyor?.busy) return; // 単一フロー: プレス稼働中は止まる

  const pathLength = Math.max(0, conveyor.path.length - 1);
  const advanceEps = 1e-5;

  // pre_press の進行 (このコンベア上のみ)
  const prePress = state.works
    .filter((w) => w.conveyorId === conveyor.id && w.phase === "pre_press")
    .sort((a, b) => b.pathDist - a.pathDist);

  for (const w of prePress) {
    const prevDist = w.pathDist;
    const advance = params.conveyorSpeed * dtSec;
    let next = w.pathDist + advance;

    for (const other of prePress) {
      if (other === w) continue;
      if (other.pathDist <= w.pathDist) continue;
      const limit = other.pathDist - MIN_WORK_SPACING;
      if (next > limit) next = limit;
    }

    const pressPathDist = pressMachineForConveyor?.pathDist ?? -1;
    if (pressMachineForConveyor && pressPathDist >= 0 && w.pathDist < pressPathDist && next >= pressPathDist) {
      if (!pressMachineForConveyor.busy && ctx.permissions.pressEnabled) {
        w.phase = "pressing";
        w.pathDist = pressPathDist;
        w.phaseTimer = 0;
        w.currentStallSec = 0;
        pressMachineForConveyor.busy = true;
        pressMachineForConveyor.currentWorkId = w.id;
        pressMachineForConveyor.progress = 0;
        pressMachineForConveyor.processingTime = params.pressProcessingTime;
        continue;
      }
      next = pressPathDist - 0.001;
    }

    w.pathDist = Math.max(0, next);
    accumulateStall(w, prevDist, dtSec, advanceEps);
  }

  // post_press の進行 — 終端に達したら隣接コンベア転送 or バッファに入る
  const postPress = state.works
    .filter((w) => w.conveyorId === conveyor.id && w.phase === "post_press")
    .sort((a, b) => b.pathDist - a.pathDist);

  for (const w of postPress) {
    const prevDist = w.pathDist;
    const advance = params.conveyorSpeed * dtSec;
    let next = w.pathDist + advance;

    // 前方の post_press による制限 (このコンベア上のみ)
    for (const other of postPress) {
      if (other === w) continue;
      if (other.pathDist <= w.pathDist) continue;
      const limit = other.pathDist - MIN_WORK_SPACING;
      if (next > limit) next = limit;
    }

    if (next >= pathLength) {
      // P3: 隣接コンベアがあれば転送 (path[end] と隣接する path[0] を持つ別コンベア)
      const adjacent = findAdjacentDownstreamConveyor(state, conveyor);
      if (adjacent) {
        w.conveyorId = adjacent.id;
        w.phase = "pre_press"; // 新コンベアでまた進む
        w.pathDist = 0;
        w.phaseTimer = 0;
        w.currentStallSec = 0;
        continue;
      }
      // 隣接無し → バッファへ入る
      const emptySlot = findEmptyBufferSlotOnConveyor(state, conveyor);
      if (emptySlot !== null) {
        w.phase = "in_buffer";
        w.bufferSlot = emptySlot;
        w.pathDist = pathLength;
        w.phaseTimer = 0;
        w.currentStallSec = 0;
        continue;
      }
      // バッファも満杯 → 1 タイル手前で停止
      const limit = pathLength - MIN_WORK_SPACING;
      if (next > limit) next = limit;
    }

    // 後ろ向きには動かない
    if (next < prevDist) next = prevDist;
    w.pathDist = next;
    accumulateStall(w, prevDist, dtSec, advanceEps);
  }
}

/** state.machines から指定コンベア上のプレスを返す (現状最大 1 個想定) */
function findPressMachineOnConveyor(state: SimulationState, conveyorId: string): Machine | undefined {
  for (const press of state.scene.presses) {
    if (press.conveyorId === conveyorId) {
      return state.machines.find((m) => m.id === press.id);
    }
  }
  return undefined;
}

/**
 * このコンベアの末端タイルと隣接する path[0] を持つ別コンベアを探す。
 * 直線連結のみ想定 (P3: アーム介在なしで隣接タイル自動連結)。
 */
function findAdjacentDownstreamConveyor(
  state: SimulationState,
  conveyor: { id: string; path: { col: number; row: number }[] },
): { id: string; path: { col: number; row: number }[] } | undefined {
  const endTile = conveyor.path[conveyor.path.length - 1];
  if (!endTile) return undefined;
  for (const c of state.scene.conveyors) {
    if (c.id === conveyor.id) continue;
    const startTile = c.path[0];
    if (!startTile) continue;
    const dx = Math.abs(startTile.col - endTile.col);
    const dy = Math.abs(startTile.row - endTile.row);
    if (dx + dy === 1) return c;
  }
  return undefined;
}

/** 指定コンベアのバッファ内の空きスロットを返す。空きなしなら null */
function findEmptyBufferSlotOnConveyor(
  state: SimulationState,
  conveyor: { id: string; bufferCapacity: number },
): number | null {
  const occupied = new Set<number>();
  for (const w of state.works) {
    if (w.conveyorId === conveyor.id && w.phase === "in_buffer" && w.bufferSlot !== null) {
      occupied.add(w.bufferSlot);
    }
  }
  for (let s = 0; s < conveyor.bufferCapacity; s++) {
    if (!occupied.has(s)) return s;
  }
  return null;
}

/** 前フレームから前進していなければ停止時間を累積、前進していればリセットする */
function accumulateStall(w: Work, prevDist: number, dtSec: number, eps: number): void {
  const moved = w.pathDist - prevDist;
  if (moved > eps) {
    w.currentStallSec = 0;
  } else {
    w.currentStallSec += dtSec;
    w.dwellTime += dtSec;
  }
}

function updatePress(state: SimulationState, _params: SimulationParams, dtSec: number): void {
  // P3: 各プレスを独立に処理
  for (const press of state.machines) {
    updateSinglePress(state, press, dtSec);
  }
}

function updateSinglePress(state: SimulationState, press: Machine, dtSec: number): void {
  if (!press.busy || press.currentWorkId === null) {
    press.progress = 0;
    return;
  }
  const work = state.works.find((w) => w.id === press.currentWorkId);
  if (!work) {
    press.busy = false;
    press.currentWorkId = null;
    press.progress = 0;
    return;
  }
  work.phaseTimer += dtSec;
  press.progress = Math.min(1, work.phaseTimer / press.processingTime);

  if (work.phaseTimer >= press.processingTime) {
    work.phase = "post_press";
    // press の pathDist (このプレスは press.pathDist で記録済み) の直後に置く
    work.pathDist = press.pathDist + 0.001;
    work.phaseTimer = 0;
    press.busy = false;
    press.currentWorkId = null;
    press.progress = 0;
  }
}

/**
 * アームのアングル定義。アームが (col, row) のとき、armAngle が θ なら腕の先端は
 * 画面 (sin θ, -cos θ) 方向を向く。タイル方向単位ベクトルを用いて、
 * 「西 (col-1)」「南 (row+1)」を指す角度を計算する。
 *
 * 西 (col-1, 0): screen 方向 (-hw, -hh) → θ_west = atan2(-hw, hh)
 * 南 (0, row+1): screen 方向 (-hw,  hh) → θ_south = atan2(-hw, -hh)
 *
 * iso 比 hw:hh = 2:1 なので具体値は概ね -1.107 (-63.4°) と -2.034 (-116.6°)。
 */
const ARM_PICKUP_ANGLE_WEST = -Math.atan2(2, 1); // ≈ -1.107 rad
const ARM_PLACE_ANGLE_SOUTH = -Math.PI + Math.atan2(2, 1); // ≈ -2.034 rad
/** アイドル時の角度（中間付近を向ける）。ピックアップ前の "待機" ポーズ。 */
const ARM_IDLE_ANGLE = (ARM_PICKUP_ANGLE_WEST + ARM_PLACE_ANGLE_SOUTH) / 2;

function updateArm(state: SimulationState, params: SimulationParams, dtSec: number): void {
  // P3: N アームを独立に走らせる。各 arm は scene.arms[i] の pickFromTile / placeToTile から
  // 対応するコンベア (バッファソース) と transport (置き先) を引き当てる。
  for (const armRuntime of state.arms) {
    updateSingleArm(state, armRuntime, params, dtSec);
  }
}

/**
 * scene 上のタイル等価判定。col/row 整数比較。
 * scene.ts の tileEquals を参照しても良いが、循環依存回避のためここで再定義。
 */
function tilesEqual(a: { col: number; row: number }, b: { col: number; row: number }): boolean {
  return a.col === b.col && a.row === b.row;
}

function updateSingleArm(state: SimulationState, armRuntime: RobotArm, params: SimulationParams, dtSec: number): void {
  const armScene = state.scene.arms.find((a) => a.id === armRuntime.id);
  if (!armScene) {
    // scene からアームが消されている (エディタ削除直後等) → idle で待機
    armRuntime.phase = "idle";
    armRuntime.heldWorkId = null;
    armRuntime.armAngle = ARM_IDLE_ANGLE;
    return;
  }

  // この arm の pickFromTile に該当するコンベア (path 末端タイルが pickFromTile)
  const sourceConveyor = state.scene.conveyors.find((c) => {
    const last = c.path[c.path.length - 1];
    return last && tilesEqual(last, armScene.pickFromTile);
  });

  // この arm の placeToTile に該当する transport
  const targetTransportScene = state.scene.transports.find((t) => tilesEqual(t.parking, armScene.placeToTile));
  const targetTransportRuntime = targetTransportScene
    ? state.transports.find((t) => t.id === targetTransportScene.id)
    : null;

  if (armRuntime.phase === "idle") {
    if (!sourceConveyor || !targetTransportScene || !targetTransportRuntime) {
      armRuntime.armAngle = ARM_IDLE_ANGLE;
      return;
    }
    // ソースコンベアのバッファ slot 0 のワークだけを対象に
    const workAtFront = state.works.find(
      (w) => w.conveyorId === sourceConveyor.id && w.phase === "in_buffer" && w.bufferSlot === 0,
    );
    const transportReady =
      targetTransportRuntime.phase === "idle_at_line" &&
      targetTransportRuntime.cargo.length < targetTransportScene.capacity;
    if (workAtFront && transportReady) {
      armRuntime.phase = "picking";
      armRuntime.timer = 0;
      armRuntime.heldWorkId = workAtFront.id;
      workAtFront.phase = "being_picked";
      workAtFront.bufferSlot = null;
      shiftBufferSlots(state, sourceConveyor.id);
    } else {
      armRuntime.armAngle = ARM_IDLE_ANGLE;
    }
    return;
  }

  armRuntime.timer += dtSec;
  const halfTime = params.armTime / 2;

  if (armRuntime.phase === "picking") {
    const t = Math.min(1, armRuntime.timer / halfTime);
    armRuntime.armAngle = ARM_IDLE_ANGLE + (ARM_PICKUP_ANGLE_WEST - ARM_IDLE_ANGLE) * t;
    if (armRuntime.timer >= halfTime) {
      armRuntime.phase = "placing";
      armRuntime.timer = 0;
    }
    return;
  }

  if (armRuntime.phase === "placing") {
    const t = Math.min(1, armRuntime.timer / halfTime);
    armRuntime.armAngle = ARM_PICKUP_ANGLE_WEST + (ARM_PLACE_ANGLE_SOUTH - ARM_PICKUP_ANGLE_WEST) * t;
    if (armRuntime.timer >= halfTime) {
      const w = state.works.find((x) => x.id === armRuntime.heldWorkId);
      if (w) {
        w.phase = "on_transport";
        w.phaseTimer = 0;
        w.bufferSlot = null;
      }
      // 置き先の transport の cargo に追加
      if (armRuntime.heldWorkId !== null && targetTransportRuntime) {
        targetTransportRuntime.cargo.push(armRuntime.heldWorkId);
      }
      armRuntime.phase = "idle";
      armRuntime.heldWorkId = null;
      armRuntime.timer = 0;
      armRuntime.armAngle = ARM_IDLE_ANGLE;
    }
  }
}

/**
 * 指定コンベアのバッファ後ろのワーク（slot 1, 2, ...）を 1 つずつ前へ詰める。
 * arm が slot 0 を取ると即座に呼ばれ、視覚的にも前進しているように見える。
 */
function shiftBufferSlots(state: SimulationState, conveyorId: string): void {
  for (const w of state.works) {
    if (w.conveyorId === conveyorId && w.phase === "in_buffer" && w.bufferSlot !== null && w.bufferSlot > 0) {
      w.bufferSlot -= 1;
    }
  }
}

function updateTransport(state: SimulationState, params: SimulationParams, dtSec: number): void {
  // P3: 各搬送ロボを独立に走らせる。
  for (const transportRuntime of state.transports) {
    updateSingleTransport(state, transportRuntime, params, dtSec);
  }
}

function updateSingleTransport(
  state: SimulationState,
  t: TransportRobot,
  params: SimulationParams,
  dtSec: number,
): void {
  const transportScene = state.scene.transports.find((s) => s.id === t.id);
  // scene 側から消えた場合 (P5 で追加予定の動的削除) は処理スキップ
  if (!transportScene) return;

  const parking = transportScene.parking;
  const route = transportScene.route.length >= 2 ? transportScene.route : [parking, transportScene.unloading];
  const capacity = transportScene.capacity;

  if (t.phase === "idle_at_line") {
    t.position.col = parking.col;
    t.position.row = parking.row;
    // 出発判定: cargo が積載上限に達したら出発
    if (t.cargo.length >= capacity) {
      t.phase = "to_unload";
      t.waypointIndex = 1;
    }
    return;
  }

  if (t.phase === "to_unload" || t.phase === "returning") {
    const waypointList = t.phase === "to_unload" ? route : [...route].reverse();
    const target = waypointList[t.waypointIndex];
    if (!target) {
      if (t.phase === "to_unload") {
        t.phase = "unloading";
        t.unloadTimer = 0;
        // 全 cargo のフェーズを unloading に切り替える（個別にフェードする）
        for (const id of t.cargo) {
          const w = state.works.find((x) => x.id === id);
          if (w) {
            w.phase = "unloading";
            w.phaseTimer = 0;
            w.pathDist = UNLOAD_TILE.col;
          }
        }
      } else {
        t.phase = "idle_at_line";
        t.waypointIndex = 0;
      }
      return;
    }

    const dx = target.col - t.position.col;
    const dy = target.row - t.position.row;
    const dist = Math.hypot(dx, dy);
    const step = params.transportSpeed * dtSec;

    if (dist <= step) {
      t.position.col = target.col;
      t.position.row = target.row;
      t.waypointIndex += 1;
    } else {
      t.position.col += (dx / dist) * step;
      t.position.row += (dy / dist) * step;
    }
    return;
  }

  if (t.phase === "unloading") {
    t.unloadTimer += dtSec;
    if (t.unloadTimer >= UNLOAD_DURATION_SEC) {
      // 全 cargo を完成扱いで撤去
      for (const id of t.cargo) {
        const w = state.works.find((x) => x.id === id);
        if (w) {
          const cycle = state.time - w.createdAt;
          state.stats.lastCycleTimeSec = cycle;
          state.stats.completedCount += 1;
          state.stats.recentCompletionTimes.push(state.time);
          state.works = state.works.filter((x) => x.id !== w.id);
        }
      }
      t.cargo = [];
      t.phase = "returning";
      t.waypointIndex = 1;
    }
  }
}

function updateStats(state: SimulationState): void {
  const cutoff = state.time - 60;
  state.stats.recentCompletionTimes = state.stats.recentCompletionTimes.filter((tt) => tt >= cutoff);
  state.stats.throughputPerMin = state.stats.recentCompletionTimes.length;
}

/** いずれかのプレス機が稼働中か */
function isAnyPressBusy(state: SimulationState): boolean {
  return state.machines.some((m) => m.kind === "press" && m.busy);
}

/**
 * 指定タイル位置の描画用: ライン経路上の pathDist は row=LOADING_TILE.row 固定、col = LOADING_TILE.col + pathDist。
 */
export function pathDistToTilePos(pathDist: number): { col: number; row: number } {
  return {
    col: LOADING_TILE.col + pathDist,
    row: LOADING_TILE.row,
  };
}
